import { Controller } from '@hotwired/stimulus'

export default class OptimisticLikeController extends Controller {
  static targets = ['count', 'icon', 'link']

  static values = {
    liked: Boolean,
    count: Number,
    likedIcon: String,
    unlikedIcon: String,
    likeUrl: String,
    unlikeUrl: String
  }

  connect() {
    this.confirmedLiked = this.likedValue
    this.syncing = false
  }

  toggle(event) {
    event.preventDefault()

    this.renderState(!this.likedValue)
    this.syncState()
  }

  async syncState() {
    if (this.syncing) return

    this.syncing = true
    let latestStream = null

    try {
      while (this.confirmedLiked !== this.likedValue) {
        const requestedState = this.likedValue
        latestStream = await this.persistState(requestedState)
        this.confirmedLiked = requestedState
      }

      if (latestStream) Turbo.renderStreamMessage(latestStream)
    } catch (error) {
      if (this.likedValue !== this.confirmedLiked) {
        this.renderState(this.confirmedLiked)
      }
      if (latestStream) Turbo.renderStreamMessage(latestStream)
      console.error('Unable to update like:', error)
    } finally {
      this.syncing = false

      if (this.confirmedLiked !== this.likedValue) this.syncState()
    }
  }

  async persistState(liked) {
    const response = await fetch(
      liked ? this.likeUrlValue : this.unlikeUrlValue,
      {
        method: liked ? 'POST' : 'DELETE',
        headers: {
          Accept: 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': document.querySelector(
            'meta[name="csrf-token"]'
          )?.content || '',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      }
    )

    if (!response.ok) {
      throw new Error(`Like request failed with status ${response.status}`)
    }

    if (response.redirected) throw new Error('Like request was redirected')

    const stream = await response.text()
    if (!stream.trim()) throw new Error('Like request returned no update')

    return stream
  }

  renderState(liked) {
    this.countValue = Math.max(
      0,
      this.countValue + (liked ? 1 : -1)
    )
    this.likedValue = liked
    this.updateElements()
  }

  updateElements() {
    this.countTargets.forEach(element => {
      element.textContent = this.countValue
    })

    if (this.hasIconTarget) {
      this.iconTarget.src = this.likedValue
        ? this.likedIconValue
        : this.unlikedIconValue
    }

    this.linkTarget.setAttribute('aria-pressed', String(this.likedValue))
  }
}
